import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _excluded = ["children"],
    _excluded2 = ["children", "onKeyDown", "triggerClickEventAsKeyDown", "triggerKeys"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { KEY_CODES, useShareForwardedRef } from "./../../../utilities";
import { applyStateStyles } from "./applyStateStyles";
import { jsx as _jsx } from "react/jsx-runtime";
var StyledElementStateWrapper = styled(function (_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.cloneElement(children, props);
}).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['isActive', 'isDisabled', 'shouldPreventDefault', 'showOutline', 'stateStyles', 'styledSelector', 'useActiveStyles', 'useDefaultStyles', 'useFocusStyles', 'useHoverStyles'].includes(prop);
  }
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  && {\n    outline: none;\n    ", "\n\n    ", "\n  }\n"])), function (_ref2) {
  var isDisabled = _ref2.isDisabled,
      stateStyles = _ref2.stateStyles,
      useActiveStyles = _ref2.useActiveStyles,
      useDefaultStyles = _ref2.useDefaultStyles,
      useHoverStyles = _ref2.useHoverStyles;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n        ", "\n      "])), applyStateStyles({
    isDisabled: isDisabled,
    stateStyles: stateStyles,
    useActiveStyles: useActiveStyles,
    useDefaultStyles: useDefaultStyles,
    useHoverStyles: useHoverStyles
  }));
}, function (_ref3) {
  var isActive = _ref3.isActive,
      isDisabled = _ref3.isDisabled,
      showOutline = _ref3.showOutline,
      stateStyles = _ref3.stateStyles,
      useFocusStyles = _ref3.useFocusStyles;
  return !isDisabled && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n        &:focus {\n          outline: none;\n          ", ";\n        }\n\n        ", ";\n      "])), !isActive && showOutline && useFocusStyles && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n            ", ";\n          "])), stateStyles.focus), isActive && css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n          ", ";\n        "])), stateStyles.active));
});

var ElementStateWrapper = function ElementStateWrapper(_ref4) {
  var children = _ref4.children,
      onKeyDown = _ref4.onKeyDown,
      triggerClickEventAsKeyDown = _ref4.triggerClickEventAsKeyDown,
      triggerKeys = _ref4.triggerKeys,
      other = _objectWithoutProperties(_ref4, _excluded2);

  var defaultRef = useRef(null);
  var ref = useShareForwardedRef(children.ref || defaultRef);
  var isFocusable = other.useFocusStyles;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isActive = _useState2[0],
      setIsActive = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showOutline = _useState4[0],
      setShowOutline = _useState4[1];

  var shouldTriggerAction = function shouldTriggerAction(event) {
    var eventKeyCode = event.keyCode || event.which || 0;
    return isFocusable && triggerClickEventAsKeyDown && triggerKeys.includes(eventKeyCode);
  };

  var elementFocusProps = isFocusable ? {
    tabIndex: -1
  } : {};

  var handleKeyDown = function handleKeyDown(event) {
    if (shouldTriggerAction(event)) {
      setShowOutline(false);
    }

    onKeyDown(event);
  };

  var handleKeyUp = function handleKeyUp(event) {
    if (shouldTriggerAction(event)) {
      var hasRef = ref && ref.current;

      if (hasRef) {
        setIsActive(true);
        ref.current.click();
      }
    }

    setIsActive(false);
    setShowOutline(true);
  };

  var handleMouseDown = function handleMouseDown() {
    setShowOutline(false);
  };

  return /*#__PURE__*/_jsx(StyledElementStateWrapper, _objectSpread(_objectSpread({
    isActive: isActive,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onMouseDown: handleMouseDown,
    showOutline: showOutline,
    tabIndex: isFocusable ? 0 : -1
  }, other), {}, {
    children: /*#__PURE__*/React.cloneElement(children, _objectSpread({
      ref: ref
    }, elementFocusProps))
  }));
};

ElementStateWrapper.displayName = "ElementStateWrapper";
ElementStateWrapper.propTypes = {
  /** Content inserted between component tags. Content must be wrapped in a single element. */
  children: PropTypes.node.isRequired,

  /** Uses disabled styling if such exists and prevents other state styles from being applied */
  isDisabled: PropTypes.bool,

  /** Callback that is called on key down */
  onKeyDown: PropTypes.func,

  /** If true uses event.preventDefault and event.stopPropagation after triggerKeys are pressed  */
  shouldPreventDefault: PropTypes.bool,

  /** Element state styles applied on user interaction */
  stateStyles: PropTypes.shape({
    /** Specifies default styling applied to the element */
    "default": PropTypes.array,

    /** Specifies disabled state styles applied when element is disabled */
    disabled: PropTypes.array,

    /** Specifies focus state styles applied when element is focused */
    focus: PropTypes.array,

    /** Specifies hover state styles applied when element is hovered */
    hover: PropTypes.array
  }).isRequired,

  /** Specifies to which component should the styles be applied to; Requires a Styled Component */
  styledSelector: PropTypes.elementType,

  /** If true, triggers onClick callback if key matches triggerKeys values */
  triggerClickEventAsKeyDown: PropTypes.bool,

  /** Specifies keys which trigger element click event */
  triggerKeys: PropTypes.arrayOf(PropTypes.number),

  /** Specifies whether active styles should be used */
  useActiveStyles: PropTypes.bool,

  /** Specifies whether default styles should be used */
  useDefaultStyles: PropTypes.bool,

  /** Specifies whether focus styles should be used */
  useFocusStyles: PropTypes.bool,

  /** Specifies whether hover styles should be used */
  useHoverStyles: PropTypes.bool
};
ElementStateWrapper.defaultProps = {
  isDisabled: false,
  onKeyDown: function onKeyDown() {},
  shouldPreventDefault: false,
  styledSelector: undefined,
  triggerClickEventAsKeyDown: false,
  triggerKeys: [KEY_CODES.ENTER, KEY_CODES.SPACE],
  useActiveStyles: true,
  useDefaultStyles: true,
  useFocusStyles: true,
  useHoverStyles: true
};
export { ElementStateWrapper, StyledElementStateWrapper };